import {
  legacyPresetIdFromRegistrySlug,
  normalizeScenarioId,
  parseRegistryScenarioId,
  registryScenarioId,
} from './vectorScenarioRegistry';

/** Map a saved pill id onto the id used in the live pill list (legacy bare vs scenario: slug). */
function resolveControlsPillId(raw, allowedSet) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return null;
  if (allowedSet.has(trimmed)) return trimmed;

  const normalized = normalizeScenarioId(trimmed);
  if (allowedSet.has(normalized)) return normalized;

  const slug = parseRegistryScenarioId(normalized)
    || parseRegistryScenarioId(trimmed)
    || legacyPresetIdFromRegistrySlug(trimmed);
  if (!slug) return null;

  const legacyId = legacyPresetIdFromRegistrySlug(slug);
  if (legacyId && allowedSet.has(legacyId)) return legacyId;

  const registryId = registryScenarioId(slug);
  if (allowedSet.has(registryId)) return registryId;

  return null;
}

/** "Custom" always sits at the far right, after every strategy (including newly added ones). */
function pinCustomLast(pills) {
  if (!pills.includes('custom')) return pills;
  return [...pills.filter((id) => id !== 'custom'), 'custom'];
}

/** Apply a saved admin pill order; unknown ids append in their default order. */
export function applyControlsPillOrder(pills, savedOrder) {
  if (!Array.isArray(pills) || !pills.length) return pills || [];
  if (!Array.isArray(savedOrder) || !savedOrder.length) return pinCustomLast(pills);
  const allowed = new Set(pills);
  const seen = new Set();
  const ordered = [];
  savedOrder.forEach((raw) => {
    const id = resolveControlsPillId(raw, allowed);
    if (!id || seen.has(id)) return;
    seen.add(id);
    ordered.push(id);
  });
  pills.forEach((id) => {
    if (!seen.has(id)) ordered.push(id);
  });
  return pinCustomLast(ordered);
}

export const SCENARIO_VISIBILITY = {
  PUBLIC: 'public',
  INTERNAL: 'internal',
  HIDDEN: 'hidden',
};

export const SCENARIO_VISIBILITY_OPTIONS = [
  { id: SCENARIO_VISIBILITY.PUBLIC, label: 'Public' },
  { id: SCENARIO_VISIBILITY.INTERNAL, label: 'Internal' },
  { id: SCENARIO_VISIBILITY.HIDDEN, label: 'Hidden' },
];

export function normalizeScenarioVisibility(raw, fallback = SCENARIO_VISIBILITY.PUBLIC) {
  const v = String(raw || fallback).trim().toLowerCase();
  if (v === SCENARIO_VISIBILITY.INTERNAL || v === SCENARIO_VISIBILITY.HIDDEN) return v;
  return SCENARIO_VISIBILITY.PUBLIC;
}

/** Stable keys for stock rows in the Vector Capital composition basket. */
export const STOCK_BASKET_KEYS = {
  EQUITY_PORTFOLIO_1X: 'equity_portfolio_1x',
  EQUITY_PORTFOLIO_225X: 'equity_portfolio_225x',
  OG_EQUITY_PORTFOLIO_1X: 'og_equity_portfolio_1x',
  OG_EQUITY_PORTFOLIO_225X: 'og_equity_portfolio_225x',
  KAIROS_STANDARD: 'kairos_standard',
  KAIROS_AGGRESSIVE: 'kairos_aggressive',
  VECTOR_CAPITAL_JOURNAL: 'vector_capital_journal',
};

export const DYNAMIC_SCENARIO_PREFIX = {
  EQUITY_ALGO: 'dyn_eq:',
  FUTURES: 'dyn_fut:',
};

/** A "Vector Capital — TICKER (1H Algo)" stock algo (current `100` or `— OG`). */
export function isVectorCapitalStockAlgo(name) {
  return /^Vector Capital\s*—.*\(1H Algo\)/i.test(String(name || ''));
}

/**
 * Friendly admin label for a stock-algo strategy:
 *   "Vector Capital — AAPL (1H Algo)"        → "AAPL — 100"
 *   "Vector Capital — AAPL (1H Algo) — OG"   → "AAPL — OG"
 *   "Vector Capital — TSLA - PYTH (1H Algo)" → "TSLA - PYTH — 100"
 * Non stock-algo names pass through unchanged. The "100" / "OG" suffix mirrors the
 * webhook variant the strategy consumes (current Equity feed vs the OG feed).
 */
export function prettyStockAlgoLabel(name) {
  const raw = String(name || '');
  if (!isVectorCapitalStockAlgo(raw)) return raw;
  const isOg = /—\s*OG\s*$/i.test(raw);
  const core = raw
    .replace(/\s*—\s*OG\s*$/i, '')
    .replace(/^Vector Capital\s*—\s*/i, '')
    .replace(/\s*\(1H Algo\)\s*$/i, '')
    .trim();
  return `${core} — ${isOg ? 'OG' : '100'}`;
}

/** Stable storage key for an individual equity algo (matches server item_key). */
export function equityAlgoBasketKey(name) {
  return `algo:${String(name || '').trim().toLowerCase().replace(/\s+/g, '_')}`;
}

export function equityAlgoNameFromKey(key, catalog = []) {
  const normalized = String(key || '').trim().toLowerCase();
  const match = (catalog || []).find((row) => {
    const name = typeof row === 'string' ? row : row?.name;
    return equityAlgoBasketKey(name) === normalized;
  });
  if (!match) return null;
  return typeof match === 'string' ? match : match.name;
}

export function dynamicEquityScenarioId(key) {
  return `${DYNAMIC_SCENARIO_PREFIX.EQUITY_ALGO}${key}`;
}

export function dynamicFuturesScenarioId(name) {
  return `${DYNAMIC_SCENARIO_PREFIX.FUTURES}${String(name || '').trim()}`;
}

export function parseDynamicScenarioId(id) {
  const raw = String(id || '');
  if (raw.startsWith(DYNAMIC_SCENARIO_PREFIX.EQUITY_ALGO)) {
    return { kind: 'equity_algo', key: raw.slice(DYNAMIC_SCENARIO_PREFIX.EQUITY_ALGO.length) };
  }
  if (raw.startsWith(DYNAMIC_SCENARIO_PREFIX.FUTURES)) {
    return { kind: 'futures', name: raw.slice(DYNAMIC_SCENARIO_PREFIX.FUTURES.length) };
  }
  return null;
}

/** Individual stock algos (AAPL/TSLA/etc.) — not pills, portfolios, or test feeds. */
export function isIndividualEquityAlgo(strategy) {
  const name = String(strategy?.name || '').trim();
  if (!name || strategy?.isEquityPortfolio) return false;
  if (strategy?.isWebhookTest || strategy?.isOrderTest) return false;
  if (strategy?.registryBacked || strategy?.scenarioSlug) return false;
  if (/webhook test|btc test|master multi account/i.test(name)) return false;
  if (/\bQLD\b|\bTQQQ\b/i.test(name)) return false;
  if (/long equity/i.test(name)) return false;
  if (/^kairos$/i.test(name)) return false;
  if (/kairos equity (standard|aggressive)/i.test(name)) return false;
  if (/vector capital journal/i.test(name)) return false;
  if (strategy?.scenarioSlug === 'vector_capital_journal') return false;
  // Only the Vector Capital hourly stock feeds (and their OG copies) belong here.
  // Futures trade journals (MW2, PrimeBeef, …) must not leak into this bucket.
  return isVectorCapitalStockAlgo(name);
}

function kairosFlavorFromRegistry({ slug, strategyName, displayName } = {}) {
  const s = String(slug || '').trim();
  const name = String(strategyName || displayName || '').trim();
  const upper = name.toUpperCase();
  if (s === 'vc_core_aggressive'
    || /\bTQQQ\b/.test(upper)
    || /KAIROS EQUITY AGGRESSIVE/.test(upper)) {
    return 'aggressive';
  }
  if (s === 'vc_core_standard'
    || /\bQLD\b/.test(upper)
    || /KAIROS EQUITY STANDARD/.test(upper)
    || /^KAIROS$/i.test(name)) {
    return 'standard';
  }
  return null;
}

function equityNameMatchesKairosFlavor(equityName, flavor) {
  const n = String(equityName || '').toUpperCase();
  if (flavor === 'aggressive') {
    return n.includes('TQQQ') || /KAIROS EQUITY AGGRESSIVE/.test(n);
  }
  if (flavor === 'standard') {
    return n.includes('QLD') || /KAIROS EQUITY STANDARD/.test(n);
  }
  return false;
}

/** Prefer live webhook overlay rows over the CSV baseline tagged with the same slug. */
function preferRegistryEquityStrategyMatch(list, predicate) {
  const matches = list.filter(predicate);
  if (!matches.length) return null;
  const overlay = matches.filter((s) => s?.isKairosWebhook || s?.isNt8LiveOverlay);
  return overlay[overlay.length - 1] || matches[0];
}

/** Match a registry strategy row to its equity-curve strategy from the API feed. */
export function findEquityStrategyForRegistry(registryRow, equityStrategies = []) {
  if (!registryRow || !Array.isArray(equityStrategies)) return null;
  const config = registryRow.dataSourceConfig || registryRow.data_source_config || {};
  const slug = String(registryRow.slug || '').trim();
  const strategyName = String(config.strategyName || registryRow.displayName || '').trim();
  const displayName = String(registryRow.displayName || registryRow.display_name || '').trim();
  const list = equityStrategies;

  if (slug) {
    const bySlug = preferRegistryEquityStrategyMatch(list, (s) => s?.scenarioSlug === slug);
    if (bySlug) return bySlug;
  }

  const namesToTry = [strategyName, displayName].filter(Boolean);
  for (const candidate of namesToTry) {
    const exact = list.find(
      (s) => String(s?.name || '').trim().toLowerCase() === candidate.toLowerCase(),
    );
    if (exact) return exact;
  }

  if (strategyName) {
    try {
      const re = new RegExp(strategyName, 'i');
      const byPattern = list.find((s) => re.test(String(s?.name || '')));
      if (byPattern) return byPattern;
    } catch (_e) { /* invalid pattern */ }
  }

  if (slug) {
    const slugNorm = slug.toLowerCase();
    const slugSpaced = slug.replace(/_/g, ' ').toLowerCase();
    const bySlugName = list.find((s) => {
      const n = String(s?.name || '').trim().toLowerCase();
      return n === slugNorm || n === slugSpaced;
    });
    if (bySlugName) return bySlugName;
  }

  const kairosFlavor = kairosFlavorFromRegistry({ slug, strategyName, displayName });
  if (kairosFlavor) {
    const kairosMatch = list.find((s) => equityNameMatchesKairosFlavor(s?.name, kairosFlavor));
    if (kairosMatch) return kairosMatch;
  }

  const LEGACY_EQUITY_NAMES_BY_REGISTRY_SLUG = {
    vector_capital_journal: ['Vector Capital Journal'],
    master_multi_account: ['Master Multi Account'],
  };
  const legacyNames = LEGACY_EQUITY_NAMES_BY_REGISTRY_SLUG[slug] || [];
  for (const legacyName of legacyNames) {
    const match = list.find(
      (s) => String(s?.name || '').trim().toLowerCase() === legacyName.toLowerCase(),
    );
    if (match) return match;
  }

  if (slug === 'vector_capital_journal') {
    const journal = list.find((s) => /vector capital journal/i.test(String(s?.name || '')));
    if (journal) return journal;
  }

  return null;
}

/** Match a futures grid / basket data name (e.g. "VM1") to its registry scenario row. */
export function findRegistryRowForFuturesDataName(registryScenarioBySlug, dataName) {
  const want = String(dataName || '').trim().toLowerCase();
  if (!want || !registryScenarioBySlug) return null;
  let found = null;
  registryScenarioBySlug.forEach((row) => {
    if (String(row.category || '').toLowerCase() !== 'futures') return;
    const cfg = row.dataSourceConfig || row.data_source_config || {};
    const names = [
      cfg.strategyName,
      row.displayName,
      row.display_name,
    ].map((n) => String(n || '').trim().toLowerCase()).filter(Boolean);
    if (names.includes(want)) found = row;
  });
  return found;
}

/** Friendly label for a futures data name in the Custom composition basket. */
export function resolveFuturesDataNameDisplayLabel(dataName, registryScenarioBySlug, labels) {
  const name = String(dataName || '').trim();
  if (!name) return name;
  const row = findRegistryRowForFuturesDataName(registryScenarioBySlug, name);
  const registryLabel = String(row?.displayName || row?.display_name || '').trim();
  if (registryLabel && registryLabel.toLowerCase() !== name.toLowerCase()) return registryLabel;
  const fromBasket = labels?.futures?.[name];
  if (fromBasket?.trim()) return fromBasket.trim();
  return name;
}

/** Whether windowed views should rebase a compounding equity curve (Kairos-style). */
export function registryScenarioUsesCompoundEquity(registryRow) {
  if (!registryRow) return false;
  const slug = String(registryRow.slug || '');
  if (slug === 'vc_core_standard' || slug === 'vc_core_aggressive') return true;
  const config = registryRow.dataSourceConfig || registryRow.data_source_config || {};
  if (config.compoundEquity === true) return true;
  if (config.compoundEquity === false) return false;
  if (String(config.parser || '') === 'kairos_daily') return true;
  const mirror = config.mirrorLiveFrom;
  if (mirror && typeof mirror === 'object') {
    const src = String(mirror.entitySlug || '').toLowerCase();
    const type = String(registryRow.dataSourceType || registryRow.data_source_type || '');
    if (src === 'kairos' || type === 'webhook') return true;
  }
  if (registryRow.dataSourceType === 'equity_curve' || registryRow.data_source_type === 'equity_curve') {
    return true;
  }
  return false;
}

export function stockBasketKeyForItem(item) {
  if (!item) return null;
  const name = String(item.name || '').trim();
  if (item.isEquityPortfolio) {
    if (name === 'Equity Portfolio 1×') return STOCK_BASKET_KEYS.EQUITY_PORTFOLIO_1X;
    if (name === 'Equity Portfolio 2.25×') return STOCK_BASKET_KEYS.EQUITY_PORTFOLIO_225X;
    if (name === 'OG Equity Portfolio 1×') return STOCK_BASKET_KEYS.OG_EQUITY_PORTFOLIO_1X;
    if (name === 'OG Equity Portfolio 2.25×') return STOCK_BASKET_KEYS.OG_EQUITY_PORTFOLIO_225X;
  }
  if (name === 'Kairos Equity Standard' || name.includes('QLD')) {
    return STOCK_BASKET_KEYS.KAIROS_STANDARD;
  }
  if (name === 'Kairos Equity Aggressive' || name.includes('TQQQ')) {
    return STOCK_BASKET_KEYS.KAIROS_AGGRESSIVE;
  }
  if (name === 'Vector Capital Journal') {
    return STOCK_BASKET_KEYS.VECTOR_CAPITAL_JOURNAL;
  }
  return null;
}

export function filterStockBasketItems(items, enabledKeys) {
  if (!Array.isArray(items)) return [];
  if (!enabledKeys || !enabledKeys.length) return items;
  const enabled = new Set(enabledKeys);
  return items.filter((item) => {
    const key = stockBasketKeyForItem(item);
    return key ? enabled.has(key) : true;
  });
}

export function filterFuturesBasketItems(strategies, enabledNames) {
  if (!Array.isArray(strategies)) return [];
  if (!enabledNames || !enabledNames.length) return strategies;
  const enabled = new Set(enabledNames);
  return strategies.filter((s) => enabled.has(s.strategy));
}

function registryIdentityKeys(row) {
  const keys = new Set();
  const cfg = row?.dataSourceConfig || row?.data_source_config || {};
  [row?.slug, row?.displayName, row?.display_name, cfg.strategyName].forEach((value) => {
    const key = String(value || '').trim().toLowerCase();
    if (key) keys.add(key);
  });
  return keys;
}

/** Hide internal/hidden registry rows for members (and admin tier preview). */
export function filterRegistryScenariosForViewer(scenarios, canSeeInternal = false) {
  if (!Array.isArray(scenarios)) return scenarios;
  return scenarios.filter((row) => {
    const vis = String(row?.visibility || 'public').toLowerCase();
    if (vis === 'hidden') return false;
    if (vis === 'internal' && !canSeeInternal) return false;
    return true;
  });
}

/** Names/slugs claimed by internal registry rows — used to strip legacy dyn_fut pills. */
export function internalRegistryClaimKeys(scenarios, canSeeInternal = false) {
  if (canSeeInternal) return new Set();
  const claims = new Set();
  (scenarios || []).forEach((row) => {
    if (String(row?.visibility || '').toLowerCase() !== 'internal') return;
    registryIdentityKeys(row).forEach((key) => claims.add(key));
  });
  return claims;
}

export function filterNamesByInternalRegistryClaims(names, claimKeys) {
  if (!claimKeys?.size) return names || [];
  return (names || []).filter((name) => !claimKeys.has(String(name).trim().toLowerCase()));
}

/** All dashboard pill ids that should show the admin-only internal green ring. */
export function buildInternalScenarioIdSet(scenarios, { canSeeInternal = false } = {}) {
  const ids = new Set();
  if (!canSeeInternal || !Array.isArray(scenarios)) return ids;
  scenarios.forEach((row) => {
    if (String(row?.visibility || 'public').trim().toLowerCase() !== 'internal') return;
    const slug = String(row?.slug || '').trim();
    if (slug) {
      ids.add(registryScenarioId(slug));
      if (row.dashboardId) ids.add(String(row.dashboardId));
      const legacy = legacyPresetIdFromRegistrySlug(slug);
      if (legacy) ids.add(legacy);
    }
    const cfg = row?.dataSourceConfig || row?.data_source_config || {};
    const name = String(cfg.strategyName || row?.displayName || row?.display_name || '').trim();
    if (name) ids.add(dynamicFuturesScenarioId(name));
  });
  return ids;
}

/** Registry strategies that claim a TradesViz strategy (futures category, or any
 *  trade_journal strategy like VC Futures ⇄ Vector Capital Journal) already have
 *  their own pills — skip dyn_fut duplicates for those strategies.
 *  Pass registryFuturesClaims from basket display when enabled scenarios omit
 *  internal rows (non-admin users). */
export function filterDynamicFuturesForRegistryPresets(
  futuresNames,
  registryScenarios = [],
  registryFuturesClaims = [],
) {
  const excluded = new Set();
  (registryScenarios || []).forEach((row) => {
    const category = String(row.category || '').toLowerCase();
    const type = String(row.dataSourceType || row.data_source_type || '').toLowerCase();
    if (category !== 'futures' && type !== 'trade_journal') return;
    const cfg = row.dataSourceConfig || row.data_source_config || {};
    const name = String(cfg.strategyName || row.displayName || row.display_name || '').trim().toLowerCase();
    if (name) excluded.add(name);
  });
  (registryFuturesClaims || []).forEach((name) => {
    const key = String(name || '').trim().toLowerCase();
    if (key) excluded.add(key);
  });
  return (futuresNames || []).filter((name) => !excluded.has(String(name).trim().toLowerCase()));
}

export function filterEquityAlgoItems(strategies, enabledKeys) {
  if (!Array.isArray(strategies)) return [];
  if (!enabledKeys || !enabledKeys.length) return [];
  const enabled = new Set(enabledKeys);
  return strategies.filter((s) => enabled.has(equityAlgoBasketKey(s.name)));
}

const PRESET_ID_TO_STOCK_KEY = {
  equity_portfolio_1x: STOCK_BASKET_KEYS.EQUITY_PORTFOLIO_1X,
  equity_portfolio_225x: STOCK_BASKET_KEYS.EQUITY_PORTFOLIO_225X,
  og_equity_portfolio_1x: STOCK_BASKET_KEYS.OG_EQUITY_PORTFOLIO_1X,
  og_equity_portfolio_225x: STOCK_BASKET_KEYS.OG_EQUITY_PORTFOLIO_225X,
  vc_core_standard: STOCK_BASKET_KEYS.KAIROS_STANDARD,
  vc_core_aggressive: STOCK_BASKET_KEYS.KAIROS_AGGRESSIVE,
  vector_capital_journal: STOCK_BASKET_KEYS.VECTOR_CAPITAL_JOURNAL,
};

export const DEFAULT_STOCK_SCENARIO_COLORS = {
  [STOCK_BASKET_KEYS.EQUITY_PORTFOLIO_1X]: '#06b6d4',
  [STOCK_BASKET_KEYS.EQUITY_PORTFOLIO_225X]: '#ec4899',
  [STOCK_BASKET_KEYS.OG_EQUITY_PORTFOLIO_1X]: '#14b8a6',
  [STOCK_BASKET_KEYS.OG_EQUITY_PORTFOLIO_225X]: '#e879f9',
  [STOCK_BASKET_KEYS.KAIROS_STANDARD]: '#34d399',
  [STOCK_BASKET_KEYS.KAIROS_AGGRESSIVE]: '#f59e0b',
  [STOCK_BASKET_KEYS.VECTOR_CAPITAL_JOURNAL]: '#22d3ee',
};

export const TEST_SCENARIO_IDS = new Set(['mma_test', 'btc_test', 'jean_test']);

export const DEFAULT_TEST_SCENARIO_COLORS = {
  mma_test: '#f472b6',
  btc_test: '#f7931a',
  jean_test: '#a78bfa',
};

const SCENARIO_COLOR_PALETTE = [
  '#a78bfa', '#f472b6', '#38bdf8', '#fb923c', '#4ade80',
  '#facc15', '#22d3ee', '#c084fc', '#f87171', '#2dd4bf',
];

export function hashColorFromString(input) {
  let hash = 0;
  const str = String(input || '');
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return SCENARIO_COLOR_PALETTE[Math.abs(hash) % SCENARIO_COLOR_PALETTE.length];
}

export function normalizeHexColorInput(color, fallback = '#8b5cf6') {
  const raw = String(color || fallback).trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-f]{3}$/.test(raw)) {
    const [, r, g, b] = raw.match(/^#(.)(.)(.)$/);
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return normalizeHexColorInput(fallback, '#8b5cf6');
}

/** Hide stock-option strategy pills when disabled in Strategy Settings. */
export function filterStockScenarioIds(scenarioIds, enabledStockKeys) {
  if (!Array.isArray(scenarioIds)) return [];
  if (!Array.isArray(enabledStockKeys)) return scenarioIds;
  const enabled = new Set(enabledStockKeys);
  return scenarioIds.filter((id) => {
    const stockKey = PRESET_ID_TO_STOCK_KEY[id];
    if (!stockKey) return true;
    return enabled.has(stockKey);
  });
}

/** Hide webhook/integration test strategy pills when disabled in Strategy Settings. */
export function filterTestScenarioIds(scenarioIds, enabledTestKeys) {
  if (!Array.isArray(scenarioIds)) return [];
  if (!Array.isArray(enabledTestKeys)) {
    return scenarioIds.filter((id) => !TEST_SCENARIO_IDS.has(id));
  }
  const enabled = new Set(enabledTestKeys);
  return scenarioIds.filter((id) => {
    if (!TEST_SCENARIO_IDS.has(id)) return true;
    return enabled.has(id);
  });
}

/** Apply admin-configured display labels to a strategy pill / chart name. */
export function resolveScenarioDisplayLabel(scenarioId, labels, defaultLabel) {
  if (!labels) return defaultLabel;
  const dynamic = parseDynamicScenarioId(scenarioId);
  if (dynamic?.kind === 'futures') {
    const custom = labels.futures?.[dynamic.name];
    return custom?.trim() || defaultLabel;
  }
  if (dynamic?.kind === 'equity_algo') {
    const custom = labels.equityAlgos?.[dynamic.key];
    return custom?.trim() || defaultLabel;
  }
  const stockKey = PRESET_ID_TO_STOCK_KEY[scenarioId];
  if (stockKey) {
    const custom = labels.stocks?.[stockKey];
    return custom?.trim() || defaultLabel;
  }
  if (TEST_SCENARIO_IDS.has(scenarioId)) {
    const custom = labels.tests?.[scenarioId];
    return custom?.trim() || defaultLabel;
  }
  return defaultLabel;
}

/** Apply admin-configured display colors to chart lines, pills, and comparison table. */
export function resolveScenarioDisplayColor(scenarioId, colors, fallbackColor) {
  const fallback = typeof fallbackColor === 'function'
    ? fallbackColor(scenarioId)
    : (fallbackColor || '#8b5cf6');
  if (!colors) return fallback;
  const dynamic = parseDynamicScenarioId(scenarioId);
  if (dynamic?.kind === 'futures') {
    return colors.futures?.[dynamic.name] || hashColorFromString(`futures:${dynamic.name}`);
  }
  if (dynamic?.kind === 'equity_algo') {
    return colors.equityAlgos?.[dynamic.key] || hashColorFromString(`algo:${dynamic.key}`);
  }
  const stockKey = PRESET_ID_TO_STOCK_KEY[scenarioId];
  if (stockKey) {
    return colors.stocks?.[stockKey] || DEFAULT_STOCK_SCENARIO_COLORS[stockKey] || fallback;
  }
  if (TEST_SCENARIO_IDS.has(scenarioId)) {
    return colors.tests?.[scenarioId] || DEFAULT_TEST_SCENARIO_COLORS[scenarioId] || fallback;
  }
  return fallback;
}
